import { useEffect, useState } from 'react';
import axios from 'axios';
import { cache, getCached, isCached } from './Cache';

interface CachedGetResult<T> {
    data: T | null;
    loading: boolean;
    error: boolean;
}

// Stale-while-revalidate GET backed by sessionStorage.
//
// A cached copy (if any) renders immediately so repeat navigation stays instant, but we
// always revalidate in the background and swap in fresh data when it differs. Previously
// a cache hit skipped the network entirely for up to 6 hours, so newly published cases
// never appeared in a tab that had already loaded the site (sessionStorage survives
// reloads and is copied into tabs opened from the page).
// Pass url=null to skip fetching (e.g. while a dependent id isn't known yet).
export function useCachedGet<T = any>(url: string | null): CachedGetResult<T> {
    const [data, setData] = useState<T | null>(null);
    const [loading, setLoading] = useState(!!url);
    const [error, setError] = useState(false);

    useEffect(() => {
        if (!url) {
            setData(null);
            setLoading(false);
            return;
        }

        let cancelled = false;
        const hit = isCached(url);
        const cached = hit ? (getCached(url) as T) : null;

        if (hit) {
            setData(cached);
            setLoading(false);
        } else {
            setLoading(true);
        }
        setError(false);

        axios
            .get(url)
            .then(result => {
                if (cancelled) return;
                cache(url, result.data);
                // Avoid a pointless re-render (and re-running reveal animations) when nothing changed.
                if (!hit || JSON.stringify(result.data) !== JSON.stringify(cached)) {
                    setData(result.data);
                }
            })
            .catch(() => {
                // A failed background refresh keeps showing the cached copy.
                if (!cancelled && !hit) setError(true);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [url]);

    return { data, loading, error };
}
